import { useEffect, useState } from 'react';

export const PHONE_QUERY = '(max-width: 900px)';
export function phoneLayout(): boolean {
  return window.matchMedia?.(PHONE_QUERY).matches ?? false;
}

/** The app follows the visible viewport when a phone keyboard opens. */
export function useMobileViewport(): boolean {
  const [phone, setPhone] = useState(phoneLayout);
  useEffect(() => {
    const media = window.matchMedia?.(PHONE_QUERY);
    const viewport = window.visualViewport;
    const root = document.documentElement;
    let keyboardOpen = false;
    const update = () => {
      const isPhone = media?.matches ?? false;
      setPhone(isPhone);
      const height = viewport?.height ?? window.innerHeight;
      const inset = Math.max(0, window.innerHeight - height - (viewport?.offsetTop ?? 0));
      const active = document.activeElement;
      const editing = active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLInputElement && !['checkbox', 'radio', 'button', 'submit'].includes(active.type)) ||
        (active instanceof HTMLElement && active.isContentEditable);
      keyboardOpen = isPhone && inset > 120 && (editing || keyboardOpen);
      root.style.setProperty('--mobile-viewport-height', `${height}px`);
      root.style.setProperty('--mobile-viewport-top', `${viewport?.offsetTop ?? 0}px`);
      root.style.setProperty('--mobile-keyboard-inset', `${keyboardOpen ? inset : 0}px`);
      root.dataset.mobileKeyboard = keyboardOpen ? 'open' : 'closed';
    };
    update();
    media?.addEventListener?.('change', update);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      media?.removeEventListener?.('change', update);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
      root.style.removeProperty('--mobile-viewport-height');
      root.style.removeProperty('--mobile-viewport-top');
      root.style.removeProperty('--mobile-keyboard-inset');
      delete root.dataset.mobileKeyboard;
    };
  }, []);
  return phone;
}
